/**
 * 🎭 MULTI-ROLE USER PROFILE - Enhanced view for users with multiple roles
 * Shows unified view of user's roles, verification status, and relationships
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Avatar,
  Button,
  Divider,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
  Tabs,
  Badge,
  Tooltip,
  IconButton,
  LinearProgress,
  Alert,
  Paper,
} from '@mui/material';
import {
  Person,
  DirectionsCar,
  DriveEta,
  Verified,
  Schedule,
  CheckCircle,
  Cancel,
  Warning,
  Visibility,
  Edit,
  Message,
  Phone,
  Email,
  LocationOn,
  CalendarToday,
  TrendingUp,
  Assignment,
  Security,
  AttachMoney,
  History,
  Close,
} from '@mui/icons-material';

import type { AdminUser } from '@/services/adminApiService';
import { verificationService, AdminVerificationRequest } from '@/services/verificationService';
import VerificationDocumentViewer from '@/components/verification/VerificationDocumentViewer';

interface MultiRoleUserProfileProps {
  user: AdminUser;
  open: boolean;
  onClose: () => void;
  onApprove?: (userId: string, role: string) => void;
  onReject?: (userId: string, role: string) => void;
  onMessage?: (userId: string) => void;
}

interface UserRole {
  type: 'renter' | 'driver' | 'host';
  status: 'active' | 'pending' | 'rejected' | 'suspended';
  verificationStatus: 'none' | 'incomplete' | 'pending' | 'verified' | 'rejected';
  completionPercentage: number;
  registeredAt: string;
  verifiedAt?: string;
  canApprove: boolean;
  requirements: string[];
  missingRequirements: string[];
}

const ROLE_CONFIG = {
  renter: {
    icon: <Person />,
    label: 'Car Renter',
    color: 'primary' as const,
    description: 'Can book and rent vehicles'
  },
  driver: {
    icon: <DriveEta />,
    label: 'Personal Driver',
    color: 'success' as const,
    description: 'Available for hire as driver'
  },
  host: {
    icon: <DirectionsCar />,
    label: 'Car Host',
    color: 'secondary' as const,
    description: 'Owns and rents out vehicles'
  }
};

export const MultiRoleUserProfile: React.FC<MultiRoleUserProfileProps> = ({
  user,
  open,
  onClose,
  onApprove,
  onReject,
  onMessage
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [verificationHistory, setVerificationHistory] = useState<AdminVerificationRequest[]>([]);
  const [loadingVerifications, setLoadingVerifications] = useState(false);
  const [selectedVerification, setSelectedVerification] = useState<AdminVerificationRequest | null>(null);
  const [verificationViewerOpen, setVerificationViewerOpen] = useState(false);

  // Load verification history when dialog opens
  React.useEffect(() => {
    if (open && user.id) {
      loadVerificationHistory();
    }
  }, [open, user.id]);

  const loadVerificationHistory = async () => {
    setLoadingVerifications(true);
    try {
      const result = await verificationService.getUserVerificationHistory(user.id);
      if (result.success && result.data) {
        setVerificationHistory(result.data);
      }
    } catch (error) {
      console.error('Failed to load verification history:', error);
    } finally {
      setLoadingVerifications(false);
    }
  };

  const handleViewVerification = (verification: AdminVerificationRequest) => {
    setSelectedVerification(verification);
    setVerificationViewerOpen(true);
  };

  // Extract user roles and their statuses
  const getUserRoles = (): UserRole[] => {
    const roles: UserRole[] = [];
    
    // Always has renter role by default
    roles.push({
      type: 'renter',
      status: user.isVerified ? 'active' : 'pending',
      verificationStatus: user.isVerified ? 'verified' : 'pending',
      completionPercentage: user.isVerified ? 100 : 75,
      registeredAt: user.createdAt || new Date().toISOString(),
      verifiedAt: user.isVerified ? user.createdAt : undefined,
      canApprove: !user.isVerified,
      requirements: ['National ID', 'Phone verification', 'Email verification'],
      missingRequirements: user.isVerified ? [] : ['National ID verification']
    });

    // Driver role if applicable
    if (user.driverVerification || user.roles?.includes('driver') || user.userType === 'driver') {
      roles.push({
        type: 'driver',
        status: user.driverVerification?.status === 'approved' ? 'active' : 
                user.driverVerification?.status === 'rejected' ? 'rejected' : 'pending',
        verificationStatus: user.driverVerification?.status === 'approved' ? 'verified' :
                           user.driverVerification?.status === 'rejected' ? 'rejected' : 'pending',
        completionPercentage: user.driverVerification?.completionPercentage || 0,
        registeredAt: user.createdAt || new Date().toISOString(),
        verifiedAt: user.driverVerification?.status === 'approved' ? user.createdAt : undefined,
        canApprove: user.driverVerification?.status === 'pending_review',
        requirements: ['Driver License', 'Two Guarantors', 'Vehicle Information'],
        missingRequirements: user.driverVerification?.completionPercentage === 100 ? [] : ['Guarantor information']
      });
    }

    // Host role if applicable  
    if (user.roles?.includes('host') || user.userType === 'host') {
      roles.push({
        type: 'host',
        status: 'pending', // This would come from host vehicle API
        verificationStatus: 'pending',
        completionPercentage: 60,
        registeredAt: user.createdAt || new Date().toISOString(),
        canApprove: false,
        requirements: ['Vehicle Registration', 'Insurance Certificate', 'Vehicle Inspection'],
        missingRequirements: ['Insurance Certificate', 'Vehicle Photos']
      });
    }

    return roles;
  };

  const userRoles = getUserRoles();
  const displayName = user.driverVerification?.identity?.firstName ? 
    `${user.driverVerification.identity.firstName} ${user.driverVerification.identity.lastName}` :
    user.name || user.email.split('@')[0];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': case 'verified': return 'success';
      case 'pending': case 'incomplete': return 'warning';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const getCompletionColor = (percentage: number) => {
    if (percentage >= 90) return 'success';
    if (percentage >= 60) return 'warning';
    return 'error';
  };

  const ContactDisplay = () => (
    <Stack direction="row" spacing={2} alignItems="center">
      {user.email && (
        <Tooltip title="Email">
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Email fontSize="small" color="action" />
            <Typography variant="body2">{user.email}</Typography>
          </Stack>
        </Tooltip>
      )}
      {user.phone && (
        <Tooltip title="Phone">
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Phone fontSize="small" color="action" />
            <Typography variant="body2">{user.phone}</Typography>
          </Stack>
        </Tooltip>
      )}
      {user.location && (
        <Tooltip title="Location">
          <Stack direction="row" spacing={0.5} alignItems="center">
            <LocationOn fontSize="small" color="action" />
            <Typography variant="body2">{user.location}</Typography>
          </Stack>
        </Tooltip>
      )}
    </Stack>
  );

  const RoleOverview = () => (
    <Grid container spacing={2}>
      {userRoles.map((role, index) => (
        <Grid item xs={12} md={4} key={role.type}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Stack spacing={2}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar sx={{ bgcolor: `${ROLE_CONFIG[role.type].color}.main`, width: 32, height: 32 }}>
                      {ROLE_CONFIG[role.type].icon}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {ROLE_CONFIG[role.type].label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {ROLE_CONFIG[role.type].description}
                      </Typography>
                    </Box>
                  </Stack>
                  <Chip 
                    label={role.status} 
                    color={getStatusColor(role.status)} 
                    size="small"
                  />
                </Box>

                <Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <Typography variant="body2" color="text.secondary">
                      Verification Progress
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {role.completionPercentage}%
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={role.completionPercentage}
                    color={getCompletionColor(role.completionPercentage)}
                    sx={{ height: 8, borderRadius: 1 }}
                  />
                </Box>

                {role.missingRequirements.length > 0 && (
                  <Alert severity="warning" sx={{ py: 0.5 }}>
                    <Typography variant="caption">
                      Missing: {role.missingRequirements.join(', ')}
                    </Typography>
                  </Alert>
                )}

                {role.canApprove && onApprove && onReject && (
                  <Stack direction="row" spacing={1}>
                    <Button 
                      size="small" 
                      variant="contained" 
                      color="success"
                      startIcon={<CheckCircle />}
                      onClick={() => onApprove(user.id, role.type)}
                    >
                      Approve
                    </Button>
                    <Button 
                      size="small" 
                      variant="outlined" 
                      color="error"
                      startIcon={<Cancel />}
                      onClick={() => onReject(user.id, role.type)}
                    >
                      Reject
                    </Button>
                  </Stack>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  const RoleTimeline = () => (
    <Stack spacing={2}>
      {userRoles.map((role, index) => (
        <Box key={role.type} display="flex" alignItems="flex-start" gap={2}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 1 }}>
            <Avatar 
              sx={{ 
                bgcolor: `${getStatusColor(role.verificationStatus)}.main`,
                width: 32, 
                height: 32 
              }}
            >
              {ROLE_CONFIG[role.type].icon}
            </Avatar>
            {index < userRoles.length - 1 && (
              <Box 
                sx={{ 
                  width: 2, 
                  height: 40, 
                  bgcolor: 'divider', 
                  mt: 1 
                }} 
              />
            )}
          </Box>
          <Paper elevation={1} sx={{ p: 2, flex: 1 }}>
            <Typography variant="h6" component="h6">
              {ROLE_CONFIG[role.type].label}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Registered: {new Date(role.registeredAt).toLocaleDateString()}
            </Typography>
            {role.verifiedAt && (
              <Typography variant="body2" color="text.secondary">
                Verified: {new Date(role.verifiedAt).toLocaleDateString()}
              </Typography>
            )}
            <Chip 
              label={`${role.completionPercentage}% Complete`}
              color={getCompletionColor(role.completionPercentage)}
              size="small"
              sx={{ mt: 1 }}
            />
          </Paper>
        </Box>
      ))}
    </Stack>
  );

  const RoleDetails = () => {
    const role = userRoles[activeTab];
    if (!role) return null;

    return (
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {ROLE_CONFIG[role.type].label} Details
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>Requirements</Typography>
              <Stack spacing={1}>
                {role.requirements.map((req, idx) => (
                  <Stack key={idx} direction="row" spacing={1} alignItems="center">
                    {role.missingRequirements.includes(req) ? (
                      <Cancel color="error" fontSize="small" />
                    ) : (
                      <CheckCircle color="success" fontSize="small" />
                    )}
                    <Typography variant="body2">{req}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>Status Information</Typography>
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">Status:</Typography>
                  <Chip label={role.status} color={getStatusColor(role.status)} size="small" />
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">Verification:</Typography>
                  <Chip label={role.verificationStatus} color={getStatusColor(role.verificationStatus)} size="small" />
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">Progress:</Typography>
                  <Typography variant="body2" fontWeight="medium">{role.completionPercentage}%</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">Registered:</Typography>
                  <Typography variant="body2">{new Date(role.registeredAt).toLocaleDateString()}</Typography>
                </Stack>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ width: 48, height: 48 }}>
              {displayName.charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="h6">{displayName}</Typography>
              <Typography variant="body2" color="text.secondary">
                User ID: {user.id}
              </Typography>
            </Box>
            <Badge badgeContent={userRoles.length} color="primary">
              <Chip label="Multi-Role User" variant="outlined" />
            </Badge>
          </Stack>
          <Stack direction="row" spacing={1}>
            {onMessage && (
              <Tooltip title="Send Message">
                <IconButton onClick={() => onMessage(user.id)}>
                  <Message />
                </IconButton>
              </Tooltip>
            )}
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Stack>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          {/* Contact Information */}
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>Contact Information</Typography>
              <ContactDisplay />
            </CardContent>
          </Card>

          {/* Role Overview */}
          <Box>
            <Typography variant="h6" gutterBottom>Role Overview</Typography>
            <RoleOverview />
          </Box>

          {/* Detailed Tabs */}
          <Box>
            <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
              {userRoles.map((role, index) => (
                <Tab
                  key={role.type}
                  label={ROLE_CONFIG[role.type].label}
                  icon={ROLE_CONFIG[role.type].icon}
                />
              ))}
              <Tab
                label={
                  <Badge badgeContent={verificationHistory.length} color="primary">
                    Verification Documents
                  </Badge>
                }
                icon={<Security />}
              />
              <Tab label="Timeline" icon={<History />} />
            </Tabs>

            <Box sx={{ mt: 2 }}>
              {activeTab < userRoles.length ? (
                <RoleDetails />
              ) : activeTab === userRoles.length ? (
                // Verification Documents Tab
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Verification Documents History
                    </Typography>

                    {loadingVerifications ? (
                      <Box sx={{ py: 4, textAlign: 'center' }}>
                        <LinearProgress />
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                          Loading verification documents...
                        </Typography>
                      </Box>
                    ) : verificationHistory.length === 0 ? (
                      <Alert severity="info">
                        No verification documents found for this user.
                      </Alert>
                    ) : (
                      <Grid container spacing={2}>
                        {verificationHistory.map((verification, index) => (
                          <Grid item xs={12} md={6} key={verification.verificationId}>
                            <Card variant="outlined" sx={{ height: '100%' }}>
                              <CardContent>
                                <Stack direction="row" justifyContent="space-between" alignItems="start" mb={2}>
                                  <Box>
                                    <Typography variant="subtitle1" fontWeight={600}>
                                      {verification.documentType.replace('_', ' ').toUpperCase()}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Submitted: {new Date(verification.uploadedAt).toLocaleDateString()}
                                    </Typography>
                                  </Box>
                                  <Chip
                                    label={verification.status.replace('_', ' ').toUpperCase()}
                                    color={
                                      verification.status === 'verified' ? 'success' :
                                      verification.status === 'rejected' ? 'error' :
                                      'warning'
                                    }
                                    size="small"
                                  />
                                </Stack>

                                <Divider sx={{ my: 2 }} />

                                <Stack spacing={1}>
                                  <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="body2" color="text.secondary">
                                      Document Type:
                                    </Typography>
                                    <Typography variant="body2" fontWeight={500}>
                                      {verification.documentType.replace('_', ' ')}
                                    </Typography>
                                  </Stack>

                                  <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="body2" color="text.secondary">
                                      Country:
                                    </Typography>
                                    <Typography variant="body2" fontWeight={500}>
                                      {verification.countryCode}
                                    </Typography>
                                  </Stack>

                                  {verification.reviewedAt && (
                                    <Stack direction="row" justifyContent="space-between">
                                      <Typography variant="body2" color="text.secondary">
                                        Reviewed:
                                      </Typography>
                                      <Typography variant="body2" fontWeight={500}>
                                        {new Date(verification.reviewedAt).toLocaleDateString()}
                                      </Typography>
                                    </Stack>
                                  )}

                                  {verification.notes && (
                                    <Box mt={1}>
                                      <Typography variant="body2" color="text.secondary" gutterBottom>
                                        Admin Notes:
                                      </Typography>
                                      <Typography variant="body2" sx={{ bgcolor: 'grey.100', p: 1, borderRadius: 1 }}>
                                        {verification.notes}
                                      </Typography>
                                    </Box>
                                  )}
                                </Stack>

                                <Button
                                  fullWidth
                                  variant="outlined"
                                  startIcon={<Visibility />}
                                  onClick={() => handleViewVerification(verification)}
                                  sx={{ mt: 2 }}
                                >
                                  View Documents
                                </Button>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </CardContent>
                </Card>
              ) : (
                // Timeline Tab
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Role Timeline</Typography>
                    <RoleTimeline />
                  </CardContent>
                </Card>
              )}
            </Box>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" startIcon={<Edit />}>
          Edit User
        </Button>
      </DialogActions>
    </Dialog>

    {/* Verification Document Viewer Dialog */}
    {selectedVerification && (
      <VerificationDocumentViewer
        open={verificationViewerOpen}
        onClose={() => {
          setVerificationViewerOpen(false);
          setSelectedVerification(null);
        }}
        verificationRequest={selectedVerification}
        onApprove={async (verificationId) => {
          // Handle approval - reload verification history after approval
          await loadVerificationHistory();
          setVerificationViewerOpen(false);
          setSelectedVerification(null);
        }}
        onReject={async (verificationId) => {
          // Handle rejection - reload verification history after rejection
          await loadVerificationHistory();
          setVerificationViewerOpen(false);
          setSelectedVerification(null);
        }}
      />
    )}
  </>
  );
};